import { useRollbar } from '@rollbar/react'
import CommonButton from 'common/components/entities/Button/button'
import { BadRequest, InternalError, NetworkError } from 'common/errors'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import { usePage, usePayment } from 'publisher/store'
import { getPageId } from 'publisher/store/page/pageSelectors'
import paymentSelectors, {
  getActivePricePlan,
  getPurchaseProcessId,
} from 'publisher/store/payment/paymentSelectors'
import React from 'react'
import { useCalculateButtonStyles } from 'src/publisher/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/publisher/shared/hooks/use-theme-color/use-selected-button-color'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { TemporaryAny } from 'src/shared/types/temporary-any'
import { buyUpsell } from '../../../api/razorpayApi'
import useRazorpay from '../../../hooks/payment/useRazorpay'
import useUpsellSubmit from '../hooks/useUpsellSubmit'
import { UpsellPaymentButtonPropsType } from './UpsellPaymentButton'

function RazorpayUpsellButton({ entity }: UpsellPaymentButtonPropsType) {
  const rollbar = useRollbar()
  const { t } = useTypedTranslation()
  const purchaseProcessId = usePayment(getPurchaseProcessId)
  const pageId = usePage(getPageId)
  const { errors, setErrors, isLoading, submit } = useUpsellSubmit(entity)
  const { Razorpay, isRazorpayFetching, isRazorpayReady, generateOptions } =
    useRazorpay({ setErrors })
  const pricePlan = usePayment(getActivePricePlan)
  const product = usePayment(paymentSelectors.getProduct)

  const handleSubmit = async () => {
    if (!pricePlan && !product?.id) {
      throw Error('price plan should be defined')
    }

    await submit(async (body: TemporaryAny) => {
      try {
        const { data } = await buyUpsell(pageId, purchaseProcessId, {
          upsell_payment_form: body,
        })

        if (isRazorpayReady) {
          const razorpay = new Razorpay(
            generateOptions(data, pricePlan, false, product),
          )
          razorpay.open()
        } else {
          throw new Error('form submitted before razorpay was ready')
        }
      } catch (error) {
        if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        } else {
          rollbar.error(`Razorpay upsell failed`, error as Error)
        }
      }
    })
  }

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    buttonType: entity.buttonType,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: entity.textFontFamily,
    mobileFontFamily: entity.mobileTextFontFamily,
    fontWeight: entity.textFontWeight,
    mobileFontWeight: entity.mobileTextFontWeight,
    fontStyle: entity.textFontStyle,
    mobileFontStyle: entity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(entity.buttonType)

  return (
    <>
      <StyleWrapper
        flex
        flexColumn
        margin={entity.margin}
        mobileMargin={entity.mobileMargin}
        appearance={entity.appearance}
      >
        <CommonButton
          onClick={handleSubmit}
          disabled={isLoading || isRazorpayFetching || !isRazorpayReady}
          text={entity.text}
          border={calculatedBorder.desktop}
          mobileBorder={calculatedBorder.mobile}
          subText={entity.subText}
          padding={calculatedPadding.desktop}
          mobilePadding={calculatedPadding.mobile}
          alignSelf={entity.alignSelf}
          background={
            (entity.background ?? entity.backgroundColor) ||
            buttonBackgroundColor
          }
          mobileBackground={
            (entity.mobileBackground ?? entity.mobileBackgroundColor) ||
            buttonBackgroundColor
          }
          attrId={entity.htmlAttrId}
          width={entity.width}
          mobileWidth={entity.mobileWidth}
          textColor={entity.textColor || buttonTextColor}
          subTextColor={entity.subTextColor}
          mobileTextColor={entity.mobileTextColor || buttonTextColor}
          mobileSubTextColor={entity.mobileSubTextColor}
          textFontSize={calculatedFontSize.desktop}
          mobileTextFontSize={calculatedFontSize.mobile}
          subTextFontSize={entity.subTextFontSize}
          mobileSubTextFontSize={entity.mobileSubTextFontSize}
          textFontFamily={calculatedFontFamily.desktop}
          textFontWeight={calculatedFontWeight.desktop}
          textFontStyle={calculatedFontStyle.desktop}
          subTextFontFamily={entity.subTextFontFamily}
          subTextFontWeight={entity.subTextFontWeight}
          subTextFontStyle={entity.subTextFontStyle}
          mobileTextFontFamily={calculatedFontFamily.mobile}
          mobileTextFontWeight={calculatedFontWeight.mobile}
          mobileTextFontStyle={calculatedFontStyle.mobile}
          mobileSubTextFontFamily={entity.mobileSubTextFontFamily}
          mobileSubTextFontWeight={entity.mobileSubTextFontWeight}
          mobileSubTextFontStyle={entity.mobileSubTextFontStyle}
          boxShadow={entity.boxShadow}
          mobileBoxShadow={entity.mobileBoxShadow}
          lineHeight={calculatedLineHeight.desktop}
          mobileLineHeight={calculatedLineHeight.mobile}
          iconClassNameBefore={entity.iconClassNameBefore}
          iconClassNameAfter={entity.iconClassNameAfter}
          hover={{
            ...entity.hover,
            backgroundColor:
              entity?.hover?.backgroundColor || hoveredButtonBackgroundColor,
          }}
          mobileHover={{
            ...entity.mobileHover,
            backgroundColor:
              entity?.mobileHover?.backgroundColor ||
              entity?.hover?.backgroundColor ||
              hoveredButtonBackgroundColor,
          }}
        />
      </StyleWrapper>
      <FieldErrors errors={errors} align="center" />
    </>
  )
}

export default RazorpayUpsellButton
